// src/auth/AuthProvider.tsx (수정된 코드)
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from "react";
import axios, { AxiosError } from 'axios';
import { useNavigate } from "react-router-dom";
// jwtDecode는 토큰 복원 시 유효성 검사 목적 외에는 더 이상 사용하지 않습니다.

import { SocketProvider, useSocket } from "../context/SocketProvider";

type User = { id: string; name: string; role: string; mqttAuth: string} | null;

interface AuthContextType {
  user: User;
  login: (accessToken: string, refreshToken: string, userInfo: User) => void;
  logout: () => void;
  accessToken: string | null;
  refreshToken: string | null;
  loading: boolean;
  // *추가: 전역적으로 사용하기 위해 Axios 인스턴스를 제공하는 것이 좋습니다.
  // 이 예시에서는 global axios를 설정하는 것으로 대체합니다.
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
// const SOCKET_URL = 'http://172.16.20.112:3000'; // 로컬 서버 주소
const SOCKET_URL = "https://rek-hybrid.co.kr"; // socket.io 서버

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const socket = useSocket();

  // 토큰 갱신 중 동시 요청 방지 플래그 (선택 사항이지만 강력 권장)
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [requestsQueue, setRequestsQueue] = useState<any[]>([]);
  
/*
  // ✅ useCallback을 사용하여 안정적인 함수 보장
  const logout = useCallback(async () => {
    console.log(`[Client LOG] 🔴 LOGOUT: 모든 토큰 (AT/RT) 소멸 및 localStorage 클리어.`);
    localStorage.clear();
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    // ✅ 토큰이 사라졌으니, Axios 기본 헤더도 제거
    delete axios.defaults.headers.common['Authorization']; 
    navigate("/login");
  }, [navigate]); // navigate에 의존성 추가
*/
/*
  const logout = useCallback(async () => {
    try {
      // 1. 서버에 로그아웃 요청 (MQTT 연결 해제 요청)
      // user 객체 내에 저장된 clientId가 있다고 가정합니다.
      if (user?.mqttAuth) {
        await axios.post(`${SOCKET_URL}/api/auth/logout`, { mqttAuth: user.mqttAuth });
      }
    } catch (error) {
      console.error("서버 로그아웃 처리 중 오류 발생:", error);
    } finally {
      // 2. 서버 응답 여부와 상관없이 클라이언트 데이터는 삭제 (사용자 경험 유지)
      console.log(`[Client LOG] 🔴 LOGOUT: 모든 토큰 소멸 및 로컬 데이터 클리어.`);
      
      localStorage.clear();
      setUser(null);
      setAccessToken(null);
      setRefreshToken(null);
      
      delete axios.defaults.headers.common['Authorization'];
      navigate("/login");
    }
  }, [navigate, user]); // user 의존성 추가
*/
  const logout = useCallback(() => {
    // 1. 소켓 연결 끊기 (이것만으로 서버의 handleDisconnect -> MQTT 해제가 자동 실행됨)
    if (socket && socket.connected) {
      console.log("🔌 Disconnecting socket and MQTT session for this device...");
      socket.disconnect(); 
    }

    // 2. 클라이언트 데이터 즉시 삭제 (사용자 경험 유지)
    console.log(`[Client LOG] 🔴 LOGOUT: 모든 토큰 소멸 및 로컬 데이터 클리어.`);
    
    localStorage.clear();
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    
    delete axios.defaults.headers.common['Authorization'];
    
    // 3. 로그인 페이지로 이동
    navigate("/login");
  }, [navigate, socket]); // user 대신 socket 의존성을 넣는 것이 더 정확합니다.

  const login = (access: string, refresh: string, userInfo: User) => {
    localStorage.setItem("accessToken", access);
    localStorage.setItem("refreshToken", refresh);
    localStorage.setItem("user", JSON.stringify(userInfo));

    setUser(userInfo);
    setAccessToken(access);
    setRefreshToken(refresh);

    //console.log("액세스 토큰 발행 : ",access);
    
    // ✅ 로그인 시 Axios 기본 헤더에 Access Token 설정
    axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
  };

  // 1. 새로고침 시 localStorage에서 상태 복원 (단순화)
  // useEffect(() => {
  //   const access = localStorage.getItem("accessToken");
  //   const refresh = localStorage.getItem("refreshToken");
  //   const storedUser = localStorage.getItem("user");

  //   if (access && refresh && storedUser) {
  //       // ✅ 토큰이 존재하면 일단 상태 복원. 만료 여부는 Interceptor가 담당.
  //       setUser(JSON.parse(storedUser));
  //       setAccessToken(access);
  //       setRefreshToken(refresh);
  //       axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
  //       console.log("리프레시 토큰 발행 : ",refresh);
  //   }
  //   setLoading(false);
  // }, []); // 초기 1회만 실행

  const isConnecting = useRef(false); // 재연결 중인지 확인하는 플래그
  useEffect(() => {
    const initAuth = async () => {
      const access = localStorage.getItem("accessToken");
      const refresh = localStorage.getItem("refreshToken");
      const storedUser = localStorage.getItem("user");

      if (access && refresh && storedUser) {
        const parsedUser = JSON.parse(storedUser);
        
        // 1. 상태 복원
        setUser(parsedUser);
        setAccessToken(access);
        setRefreshToken(refresh);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
        //console.log("리프레시 토큰 발행 : ",refresh);

        // 2. ✅ MQTT 재연결 요청 추가
        // 창을 닫으면 서버의 MQTT 클라이언트도 종료되었을 것이므로 다시 연결해야 합니다.
        if (parsedUser.mqttAuth) {
          try {
            isConnecting.current = true; // 락 걸기
            //console.log("창 재오픈에 따른 MQTT 재연결 시도:", parsedUser.mqttAuth," & ",socket);
            // 기존에 만든 login API를 재활용하거나 전용 재연결 API를 호출합니다.
            await axios.post(`${SOCKET_URL}/api/auth/reconnect-mqtt`, { socketId: socket, mqttAuth: parsedUser.mqttAuth });
          } catch (err) {
            //console.error("MQTT 자동 재연결 실패2:", err);
            isConnecting.current = false; // 실패 시 다시 시도할 수 있게 락 해제
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // 2. Axios Interceptor 설정 (가장 중요)
  useEffect(() => {
    
    // 이 부분에서 `accessToken`, `refreshToken`, `logout` 함수 등을 사용합니다.
    const interceptor = axios.interceptors.response.use(
        (response) => response, // 200번대 응답은 그대로 통과
        async (error: AxiosError) => {
            const originalRequest = error.config;
            
            // 401 에러이고, 토큰 갱신 재시도 요청이 아닌 경우에만 처리
            if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry) {
                console.log(`[Client LOG] ⚠️ 401 에러 감지: Access Token 만료 추정. 갱신 시도.`);
                (originalRequest as any)._retry = true; // 재시도 플래그 설정
                
                // Refresh Token이 없으면 로그아웃 처리
                const currentRefreshToken = localStorage.getItem("refreshToken");
                if (!currentRefreshToken) {
                    console.log(`[Client LOG] 🚫 RT 없음: 로그아웃 처리.`);
                    logout();
                    return Promise.reject(error);
                }

                try {
                    // Refresh API 호출
                    const response = await axios.post(`${SOCKET_URL}/api/auth/refresh`, { 
                        refreshToken: currentRefreshToken 
                    });
                    
                    const newAccess = response.data.accessToken;
                    
                    // 1. 상태 및 저장소 업데이트
                    console.log(`[Client LOG] ✅ AT 갱신 성공: 새 AT 저장 및 요청 재시도.`);
                    localStorage.setItem("accessToken", newAccess);
                    setAccessToken(newAccess); // 상태 업데이트
                    
                    // 2. 실패했던 원래 요청의 헤더 업데이트
                    originalRequest.headers['Authorization'] = `Bearer ${newAccess}`;
                    axios.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;
                    //console.log("액세스 토큰 재발행 : ",newAccess);

                    // 3. 실패했던 원래 요청 재시도
                    return axios(originalRequest);

                } catch (refreshError) {
                    // Refresh API 호출 실패 (Refresh Token 만료 또는 유효하지 않음)
                    console.error("Refresh Token 갱신 실패:", refreshError);
                    logout(); // 강제 로그아웃
                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );

    // 컴포넌트 언마운트 시 Interceptor 제거 (클린업)
    return () => {
        axios.interceptors.response.eject(interceptor);
    };
  }, [logout]); // logout 함수에 의존성 추가

  if (loading) {
    return <div>로딩 중...</div>; // 로딩 중 화면 표시
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, accessToken, refreshToken, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook은 기존과 동일
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};